/**
 * 長く使われていないトーク（休眠の棚卸し・部屋・issue #96・docs/01 5-9）。
 *
 * この画面でできること:
 *  - 決めた日数よりメッセージの無いトークの一覧を見る（週に一度・月曜に新しくなる）
 *  - 確かめたうえで、選んだトークをまとめて「休眠」にする
 *  - 休眠にしたトークの休眠を解く
 *
 * 休眠にしたトークは、参加している方のトーク一覧に出なくなり、新しいメッセージを送れなくなります。
 * **中身（メッセージ・写真）は消えません**（保存期間の話とは別）。休眠を解くと元に戻ります。
 *
 * 決めごと（docs/06 決定22・5-2「見込み → 確認 → 実行」）:
 *  - **自動では何も休眠になりません。** 一覧を見ただけでは、何も変わりません。
 *  - まとめて休眠にする前に、確認の窓で件数と名前・元に戻せることを出します。終わったら、できた件数と
 *    できなかったトークの理由を結果の帯で出します。休眠にしたこと・解いたことは操作履歴に残ります。
 *  - **1対1はこの一覧に出ません**（管理者は1対1に手を入れない・決定27）。
 *  - 休眠を解くのは何度でもやり直せる操作なので、確認の窓は出しません（削除したトークを元に戻すのと同じ）。
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, nextStepAfter, type LoadFailure } from '../api/load-failure.js';
import type { BulkOutcome, DormantChannel, DormantChannelList } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { channelSiteLabel, channelTypeLabel } from '../channels/labels.js';
import { BulkSkippedList } from '../components/BulkSkippedList.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { count, windowSentence } from '../dormancy/labels.js';
import { formatTableDateTime } from '../format/datetime.js';
import { withReason } from '../format/sentence.js';
import { can } from '../menu/visible.js';

/** 一覧の切り替え（長く使われていないトーク／休眠にしたトーク）。 */
type Tab = 'candidates' | 'dormant';

/** 確認の窓に名前を並べる数（多いときは「ほか◯件」とまとめる）。 */
const NAMES_IN_DIALOG = 10;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: ReactNode;
}

export function DormantRoomsPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const canChangeSettings = profile?.grant ? can(profile.grant, 'settings.manage') : false;

  const [tab, setTab] = useState<Tab>('candidates');
  const [data, setData] = useState<DormantChannelList | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wakingId, setWakingId] = useState<string | null>(null);
  const [result, setResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<DormantChannelList>('/admin/dormancy/channels')
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        setSelected((current) => {
          const still = new Set(loaded.candidates.map((row) => row.id));
          return new Set([...current].filter((id) => still.has(id)));
        });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const chosen = useMemo(
    () => (data?.candidates ?? []).filter((row) => selected.has(row.id)),
    [data, selected],
  );

  const toggle = useCallback((id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  async function makeDormant(): Promise<void> {
    setBusy(true);
    try {
      const outcome = await api.post<BulkOutcome>('/admin/dormancy/channels/make-dormant', {
        body: { channel_ids: chosen.map((row) => row.id) },
      });
      setConfirming(false);
      setResult(bannerOf(outcome, new Map(chosen.map((row) => [row.id, nameOf(row)]))));
      setSelected(new Set());
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setConfirming(false);
      setResult({
        tone: 'error',
        message: withReason('休眠にできませんでした', toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusy(false);
    }
  }

  async function wake(row: DormantChannel): Promise<void> {
    setWakingId(row.id);
    setResult(null);
    try {
      await api.post(`/admin/dormancy/channels/${row.id}/wake`);
      setResult({
        tone: 'success',
        message: `「${nameOf(row)}」の休眠を解きました。`,
        nextStep:
          '参加している方のトーク一覧に戻り、またメッセージを送れます（アプリでは、次にトーク一覧が新しくなったときに戻ります）。',
      });
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(`「${nameOf(row)}」の休眠を解けませんでした`, toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setWakingId(null);
    }
  }

  return (
    <article>
      <PageHeader
        title="長く使われていないトーク"
        description="決めた日数より長く、メッセージが送られていないトークの一覧です。確かめたうえで、選んだトークをまとめて休眠にできます。休眠にしたトークは参加している方の一覧から消え、メッセージを送れなくなりますが、中身は消えません。一覧に出ても、自動では何も変わりません。"
      />

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
          reveal
        />
      ) : null}

      <div className="admin-tabs" role="group" aria-label="一覧の切り替え">
        <button
          type="button"
          className="admin-tab"
          aria-pressed={tab === 'candidates'}
          onClick={() => {
            setTab('candidates');
            setResult(null);
          }}
        >
          長く使われていないトーク{data ? `（${count(data.candidate_total)}件）` : ''}
        </button>
        <button
          type="button"
          className="admin-tab"
          aria-pressed={tab === 'dormant'}
          onClick={() => {
            setTab('dormant');
            setResult(null);
          }}
        >
          休眠にしたトーク{data ? `（${count(data.dormant_total)}件）` : ''}
        </button>
      </div>

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock label="一覧を読み込んでいます…" /> : null}

      {data && tab === 'candidates' ? (
        <>
          <p className="admin-note">
            {windowSentence(data, 'メッセージの無いトーク（1対1は出ません）')}
            {canChangeSettings ? (
              <>
                日数は<Link to="/settings/dormancy">会社の設定</Link>で変えられます。
              </>
            ) : null}
          </p>
          {data.candidates.length === 0 ? (
            <EmptyBlock label="いまは、長く使われていないトークはありません。" />
          ) : (
            <>
              {data.candidate_total > data.candidates.length ? (
                <p className="admin-note">
                  全部で{count(data.candidate_total)}件あります。いまは、長く使われていない順に
                  {count(data.candidates.length)}
                  件を出しています。休眠にすると、次のトークが出ます。
                </p>
              ) : null}
              <div className="admin-bulk-bar" role="group" aria-label="まとめて休眠にする">
                <span className="admin-bulk-count" aria-live="polite">
                  {chosen.length === 0
                    ? 'まだ選んでいません。'
                    : `${count(chosen.length)}件を選んでいます。`}
                </span>
                <Button
                  variant="secondary"
                  compact
                  onClick={() => setSelected(new Set(data.candidates.map((row) => row.id)))}
                >
                  すべて選ぶ
                </Button>
                <Button
                  variant="quiet"
                  compact
                  disabled={chosen.length === 0}
                  onClick={() => setSelected(new Set())}
                >
                  選ぶのをやめる
                </Button>
                <Button
                  variant="danger"
                  disabled={chosen.length === 0}
                  onClick={() => {
                    setResult(null);
                    setConfirming(true);
                  }}
                >
                  選んだトークを休眠にする
                </Button>
              </div>
              <DataTable label="長く使われていないトークの一覧">
                <thead>
                  <tr>
                    <th scope="col">選ぶ</th>
                    <th scope="col">名前</th>
                    <th scope="col">種類</th>
                    <th scope="col">拠点</th>
                    <th scope="col">参加人数</th>
                    <th scope="col">
                      最後の
                      <wbr />
                      メッセージ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.candidates.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <label className="ui-check" htmlFor={`dormant-room-${row.id}`}>
                          <input
                            id={`dormant-room-${row.id}`}
                            type="checkbox"
                            checked={selected.has(row.id)}
                            onChange={(event) => toggle(row.id, event.target.checked)}
                          />
                          <span className="admin-visually-hidden">「{nameOf(row)}」を選ぶ</span>
                        </label>
                      </td>
                      <th scope="row" className="ui-cell-wrap">
                        <Link to={`/rooms/${row.id}`}>{nameOf(row)}</Link>
                      </th>
                      <td>{channelTypeLabel(row.type)}</td>
                      <td className="ui-cell-soft">{channelSiteLabel(row.site_name)}</td>
                      <td>{count(row.member_count)}名</td>
                      <td className="ui-cell-date">
                        {row.last_message_at === null
                          ? `ありません（作った日時: ${formatTableDateTime(row.created_at)}）`
                          : formatTableDateTime(row.last_message_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </>
          )}
        </>
      ) : null}

      {data && tab === 'dormant' ? (
        data.dormant.length === 0 ? (
          <EmptyBlock label="休眠にしたトークはありません。" />
        ) : (
          <>
            <p className="admin-note">
              休眠を解くと、参加している方のトーク一覧に戻り、またメッセージを送れます。休眠にした日時の新しい順に並んでいます。
            </p>
            <DataTable label="休眠にしたトークの一覧">
              <thead>
                <tr>
                  <th scope="col">名前</th>
                  <th scope="col">種類</th>
                  <th scope="col">拠点</th>
                  <th scope="col">休眠にした日時</th>
                </tr>
              </thead>
              <tbody>
                {data.dormant.map((row) => (
                  <tr key={row.id}>
                    {/* 「休眠を解く」はこの画面でいちばん押すボタンなので、名前の下に置く
                        （狭い画面でも、表を横に送らずに押せる・issue #459） */}
                    <th scope="row" className="ui-cell-wrap">
                      <Link to={`/rooms/${row.id}`}>{nameOf(row)}</Link>
                      <span className="admin-row-action">
                        <Button
                          variant="secondary"
                          compact
                          busy={wakingId === row.id}
                          busyLabel="休眠を解いています…"
                          onClick={() => void wake(row)}
                        >
                          休眠を解く
                        </Button>
                      </span>
                    </th>
                    <td>{channelTypeLabel(row.type)}</td>
                    <td className="ui-cell-soft">{channelSiteLabel(row.site_name)}</td>
                    <td className="ui-cell-date">{formatTableDateTime(row.dormant_at)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </>
        )
      ) : null}

      <ConfirmDialog
        open={confirming}
        title={`${count(chosen.length)}件のトークを休眠にします`}
        message={
          <>
            {namesOf(chosen)}
            を休眠にします。参加している方のトーク一覧から消え、新しいメッセージを送れなくなります。中身（メッセージ・写真などのファイル）は消えません。
          </>
        }
        reversibility="「休眠にしたトーク」から、いつでも休眠を解けます。休眠にしたことは操作履歴に残ります。"
        reversible
        confirmLabel="休眠にする"
        busy={busy}
        onConfirm={() => void makeDormant()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}

function nameOf(row: { name: string; type: string }): string {
  return row.name === '' ? `（名前なしの${channelTypeLabel(row.type)}）` : row.name;
}

function namesOf(rows: DormantChannel[]): string {
  const shown = rows.slice(0, NAMES_IN_DIALOG).map((row) => `「${nameOf(row)}」`);
  const rest = rows.length - shown.length;
  return `${shown.join('')}${rest > 0 ? `ほか${count(rest)}件` : ''}`;
}

function bannerOf(outcome: BulkOutcome, knownNames: ReadonlyMap<string, string>): Banner {
  const done = outcome.done.length;
  const skipped = outcome.skipped.length;
  if (skipped === 0) {
    return {
      tone: 'success',
      message: `${count(done)}件のトークを休眠にしました。`,
      nextStep:
        '休眠にしたことは操作履歴に残りました。戻すときは「休眠にしたトーク」から休眠を解けます（アプリでは、次にトーク一覧が新しくなったときに消えます）。',
    };
  }
  return {
    tone: done === 0 ? 'error' : 'success',
    message:
      done === 0
        ? `選んだ${count(skipped)}件は休眠にしませんでした。`
        : `${count(done)}件のトークを休眠にしました。${count(skipped)}件は休眠にしませんでした。`,
    nextStep: <BulkSkippedList skipped={outcome.skipped} knownNames={knownNames} />,
  };
}

function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
